import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { ArrowLeft, CreditCard, History, ChevronRight, Sparkles, Gift, Copy, Share2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const COST_TABLE = [
  { action: 'First Build (full pipeline)', cost: '~48-84 credits' },
  { action: 'Code Generation (per 2,000 lines)', cost: '4 credits' },
  { action: 'Fork / Iteration', cost: '10 credits' },
  { action: 'Chat Export (.txt)', cost: '1 credit' },
  { action: 'Chat Export (.pdf)', cost: '2 credits' },
  { action: 'Self-Fix (Healer)', cost: '20 credits' },
];

export default function BuyCreditsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [packages, setPackages] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [referral, setReferral] = useState(null);
  const [referralCode, setReferralCode] = useState('');
  const [referralMsg, setReferralMsg] = useState('');

  useEffect(() => {
    // Fetch referral data
    fetch(`${API_URL}/api/referral`, { credentials: 'include' })
      .then(r => r.json()).then(setReferral).catch(() => {});
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pkgRes, histRes] = await Promise.all([
          fetch(`${API_URL}/api/credits/packages`),
          fetch(`${API_URL}/api/credits/history`, { credentials: 'include' }),
        ]);
        if (pkgRes.ok) {
          const d = await pkgRes.json();
          setPackages(d.packages || []);
        }
        if (histRes.ok) {
          const d = await histRes.json();
          setHistory(d.transactions || []);
        }
      } catch (err) {
        console.error('Failed to fetch credit data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handlePurchase = async (packageId) => {
    setPurchasing(packageId);
    try {
      const res = await fetch(`${API_URL}/api/credits/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ package_id: packageId, origin_url: window.location.origin }),
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        if (data.url) window.location.href = data.url;
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to start checkout');
      }
    } catch (err) {
      console.error('Purchase error:', err);
      alert('Something went wrong. Please try again.');
    } finally {
      setPurchasing(null);
    }
  };

  const balance = user?.credits || 0;

  return (
    <div data-testid="buy-credits-page" style={{ maxWidth: '860px', margin: '0 auto' }}>
      <button onClick={() => navigate('/')} className="btn btn-ghost" style={{ marginBottom: '1.5rem', fontSize: '0.8125rem' }} data-testid="back-button">
        <ArrowLeft size={16} /> Back
      </button>

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 700, fontFamily: 'Space Grotesk, sans-serif', marginBottom: '0.5rem' }}>
          Buy Credits
        </h1>
        <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.9375rem', marginBottom: '1.25rem' }}>
          Not on a monthly plan? Purchase credits to start building.
        </p>
        <div data-testid="credit-balance" style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.625rem',
          padding: '0.625rem 1.5rem', borderRadius: '999px',
          background: 'hsl(var(--secondary))', border: '1px solid hsl(var(--border))',
        }}>
          <CreditCard size={18} style={{ color: '#6366f1' }} />
          <span style={{ fontWeight: 700, fontSize: '1.125rem', fontFamily: 'Space Grotesk, sans-serif' }}>
            {balance}
          </span>
          <span style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.8125rem' }}>credits</span>
        </div>
      </div>

      {/* Credit Packages */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}><div className="spinner" /></div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.25rem', marginBottom: '2.5rem' }}>
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              className="card"
              data-testid={`credit-package-${pkg.id}`}
              style={{
                padding: '2rem 1.5rem',
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                textAlign: 'center',
                transition: 'transform 0.15s, border-color 0.15s',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.borderColor = '#6366f1'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = ''; }}
            >
              <div style={{ fontSize: '2.5rem', fontWeight: 800, fontFamily: 'Space Grotesk, sans-serif', color: '#6366f1', marginBottom: '0.25rem' }}>
                ${pkg.price}
              </div>

              <div style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1.25rem', color: 'hsl(var(--muted-foreground))' }}>
                {pkg.credits} credits
              </div>

              <button
                className="btn btn-secondary"
                onClick={() => handlePurchase(pkg.id)}
                disabled={purchasing !== null}
                style={{ width: '100%', justifyContent: 'center', fontSize: '0.8125rem' }}
                data-testid={`buy-${pkg.id}-btn`}
              >
                {purchasing === pkg.id
                  ? <><div className="spinner" style={{ width: '14px', height: '14px' }} /> Processing...</>
                  : <><CreditCard size={14} /> Buy</>
                }
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Cost Breakdown */}
      <div className="card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
        <h3 style={{ fontSize: '0.9375rem', fontWeight: 700, fontFamily: 'Space Grotesk, sans-serif', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Sparkles size={16} style={{ color: '#6366f1' }} />
          Credit Costs
        </h3>
        <div>
          {COST_TABLE.map((row, i) => (
            <div key={i} data-testid={`cost-row-${i}`} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '0.625rem 0',
              borderBottom: i < COST_TABLE.length - 1 ? '1px solid hsl(var(--border))' : 'none',
              fontSize: '0.8125rem',
            }}>
              <span>{row.action}</span>
              <span style={{ fontWeight: 700, fontFamily: 'Space Grotesk, sans-serif', color: '#6366f1' }}>{row.cost}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Already on a plan? */}
      <div style={{
        textAlign: 'center', padding: '1rem', marginBottom: '1.5rem',
        fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))',
      }}>
        Want unlimited builds?{' '}
        <button onClick={() => navigate('/pricing')} className="btn btn-ghost" style={{ fontSize: '0.8125rem', padding: '0.25rem 0.5rem', textDecoration: 'underline' }}>
          View monthly plans
        </button>
      </div>

      {/* Transaction History */}
      <div className="card" style={{ padding: '1.5rem' }}>
        <button
          onClick={() => setShowHistory(!showHistory)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer', width: '100%',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: 0, color: 'hsl(var(--foreground))',
          }}
          data-testid="toggle-history-btn"
        >
          <span style={{ fontSize: '0.9375rem', fontWeight: 700, fontFamily: 'Space Grotesk, sans-serif', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <History size={16} style={{ color: 'hsl(var(--muted-foreground))' }} />
            Transaction History
          </span>
          <ChevronRight size={16} style={{ transform: showHistory ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }} />
        </button>

        {/* Referral Program */}
        <div className="card" style={{ padding: '1.25rem', marginBottom: '1.5rem', background: 'linear-gradient(135deg, rgba(139,92,246,0.06), rgba(99,102,241,0.06))', border: '1px solid rgba(139,92,246,0.2)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <Gift size={18} style={{ color: '#a78bfa' }} />
            <h3 style={{ fontSize: '1rem', fontWeight: 700 }}>Refer Friends, Get Credits</h3>
          </div>
          <p style={{ fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.75rem', lineHeight: 1.6 }}>
            Share your referral link — both you and your friend get <strong>10 free credits</strong> when they sign up!
          </p>
          {referral && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
              <code data-testid="referral-link-display" style={{
                flex: 1, minWidth: '200px', fontSize: '0.75rem', padding: '0.5rem 0.75rem',
                background: 'hsl(var(--secondary))', borderRadius: '0.375rem',
                fontFamily: '"JetBrains Mono", monospace', color: '#a78bfa',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {referral.url}
              </code>
              <button
                data-testid="copy-referral-link-btn"
                className="btn btn-primary"
                onClick={() => { navigator.clipboard.writeText(referral.url); setReferralMsg('Copied!'); setTimeout(() => setReferralMsg(''), 2000); }}
                style={{ fontSize: '0.75rem', padding: '0.5rem 0.75rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
              >
                <Copy size={12} /> {referralMsg || 'Copy Link'}
              </button>
            </div>
          )}
          {referral && referral.referred_count > 0 && (
            <p style={{ fontSize: '0.75rem', color: '#10b981' }}>
              You've referred {referral.referred_count} friend{referral.referred_count > 1 ? 's' : ''} and earned {referral.total_earned} credits!
            </p>
          )}
          
          {/* Apply referral code */}
          <div style={{ marginTop: '0.75rem', borderTop: '1px solid hsl(var(--border))', paddingTop: '0.75rem' }}>
            <p style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.375rem' }}>Have a referral code?</p>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                data-testid="referral-code-input"
                type="text"
                value={referralCode}
                onChange={e => setReferralCode(e.target.value)}
                placeholder="Enter code"
                style={{
                  flex: 1, padding: '0.375rem 0.75rem', borderRadius: '0.375rem',
                  border: '1px solid hsl(var(--border))', background: 'hsl(var(--secondary))',
                  fontSize: '0.8125rem', color: 'hsl(var(--foreground))',
                }}
              />
              <button
                data-testid="apply-referral-btn"
                className="btn btn-secondary"
                style={{ fontSize: '0.75rem' }}
                onClick={async () => {
                  if (!referralCode.trim()) return;
                  try {
                    const res = await fetch(`${API_URL}/api/referral/apply`, {
                      method: 'POST', credentials: 'include',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ code: referralCode.trim() }),
                    });
                    if (res.ok) {
                      setReferralMsg('10 credits added!');
                      setReferralCode('');
                    } else {
                      const err = await res.json();
                      setReferralMsg(err.detail || 'Invalid code');
                    }
                  } catch (e) { setReferralMsg('Error applying code'); }
                  setTimeout(() => setReferralMsg(''), 3000);
                }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>

        {showHistory && (
          <div style={{ marginTop: '1rem' }}>
            {history.length === 0 ? (
              <p style={{ fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))', textAlign: 'center', padding: '1rem 0' }}>
                No transactions yet
              </p>
            ) : (
              history.slice(0, 20).map((tx, i) => (
                <div key={i} data-testid={`tx-row-${i}`} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '0.5rem 0',
                  borderBottom: i < Math.min(history.length, 20) - 1 ? '1px solid hsl(var(--border))' : 'none',
                  fontSize: '0.8125rem',
                }}>
                  <div>
                    <div style={{ fontWeight: 500 }}>{tx.description}</div>
                    <div style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))' }}>
                      {tx.created_at ? new Date(tx.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                    </div>
                  </div>
                  <span style={{
                    fontWeight: 700, fontFamily: 'Space Grotesk, sans-serif',
                    color: tx.amount > 0 ? '#10b981' : '#ef4444',
                  }}>
                    {tx.amount > 0 ? '+' : ''}{tx.amount}
                  </span>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
